import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Select, Tooltip, Spin, Tag, InputNumber } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import IssueKeyBadge from "./IssueKeyBadge";
import * as issuesApi from "../../../../core/data/redux/services/issuesApi";
import {
  fetchDependencies,
  addDependency,
  removeDependency,
} from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveCompany,
  selectDependencies,
  selectDependenciesLoading,
} from "../../../../core/data/redux/projects/selectors";
import { errMsg } from "../../helpers/errMsg.helper";
import { SETTINGS_MODAL_PROPS } from "../../helpers/modal.style";
import { notify } from "../../../../services/toast.config";

import type { AppDispatch } from "../../../../core/data/redux/store";

interface DependencyPanelProps {
  issueId: number;
  projectId: number;
}

interface IssueLite {
  id: number;
  issue_key: string;
  title: string;
  type?: string;
}

const LINK_TYPES = [
  { value: "FS", label: "Finish-to-Start (FS)" },
  { value: "FF", label: "Finish-to-Finish (FF)" },
  { value: "SS", label: "Start-to-Start (SS)" },
  { value: "SF", label: "Start-to-Finish (SF)" },
];

const DependencyPanel: React.FC<DependencyPanelProps> = ({ issueId, projectId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const activeCompany = useSelector(selectActiveCompany);
  const dependencies = useSelector(selectDependencies);
  const loading = useSelector(selectDependenciesLoading);

  const [modalOpen, setModalOpen] = useState(false);
  const [linkType, setLinkType] = useState<string>("FS");
  const [lagDays, setLagDays] = useState<number>(0);
  const [searchResults, setSearchResults] = useState<IssueLite[]>([]);
  const [selectedIssueId, setSelectedIssueId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [issueLookup, setIssueLookup] = useState<Record<number, IssueLite>>({});

  const predecessors = useMemo(
    () => dependencies.filter((d) => Number(d.successor_id) === issueId),
    [dependencies, issueId]
  );

  const successors = useMemo(
    () => dependencies.filter((d) => Number(d.predecessor_id) === issueId),
    [dependencies, issueId]
  );

  const referencedIds = useMemo(() => {
    const ids = new Set<number>();
    dependencies.forEach((d) => {
      ids.add(Number(d.predecessor_id));
      ids.add(Number(d.successor_id));
    });
    ids.delete(issueId);
    return Array.from(ids);
  }, [dependencies, issueId]);

  useEffect(() => {
    if (!activeCompany?.company_id || referencedIds.length === 0) return;
    const missing = referencedIds.filter((id) => !issueLookup[id]);
    if (missing.length === 0) return;
    issuesApi
      .searchIssues(activeCompany.company_id, activeCompany.company_type, {
        ids: missing,
        project_ids: [projectId],
        limit: missing.length,
      })
      .then((data: any) => {
        const list: IssueLite[] = Array.isArray(data?.issues) ? data.issues : [];
        if (list.length === 0) return;
        setIssueLookup((prev) => {
          const next = { ...prev };
          list.forEach((i) => {
            next[i.id] = i;
          });
          return next;
        });
      })
      .catch(() => {});
  }, [referencedIds, activeCompany, projectId, issueLookup]);

  const runSearch = useCallback(
    async (value: string) => {
      if (!activeCompany?.company_id) return;
      setSearching(true);
      try {
        const data = await issuesApi.searchIssues(
          activeCompany.company_id,
          activeCompany.company_type,
          { project_ids: [projectId], q: value, limit: 20 }
        );
        setSearchResults(
          ((data?.issues as IssueLite[]) || []).filter(
            (i) => i.id !== issueId
          )
        );
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to search issues"));
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    },
    [activeCompany, projectId, issueId]
  );

  useEffect(() => {
    if (modalOpen) {
      runSearch("");
    }
  }, [modalOpen, runSearch]);

  const handleCreateDependency = async () => {
    if (!selectedIssueId) {
      notify.error("Select an issue to depend on");
      return;
    }
    setSubmitting(true);
    try {
      await dispatch(
        addDependency({
          project_id: projectId,
          predecessor_id: selectedIssueId,
          successor_id: issueId,
          link_type: linkType as any,
          lag_days: lagDays,
        })
      ).unwrap();
      notify.success("Dependency added");
      setModalOpen(false);
      setSelectedIssueId(null);
      setSearchResults([]);
      dispatch(fetchDependencies(projectId));
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to create dependency"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDependency = async (id: number) => {
    try {
      await dispatch(removeDependency(id)).unwrap();
      notify.success("Dependency removed");
      dispatch(fetchDependencies(projectId));
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to delete dependency"));
    }
  };

  const renderIssueRow = (otherId: number) => {
    const ref = issueLookup[otherId];
    if (ref) {
      const projectKey = ref.issue_key.split("-")[0];
      return (
        <>
          <IssueKeyBadge projectKey={projectKey} issueKey={ref.issue_key} />
          <span className="flex-grow-1 text-truncate">{ref.title}</span>
        </>
      );
    }
    return (
      <>
        <IssueKeyBadge projectKey="#" issueKey={String(otherId)} />
        <span className="flex-grow-1 text-truncate text-muted">
          Issue #{otherId}
        </span>
      </>
    );
  };

  const totalDeps = predecessors.length + successors.length;

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="small text-muted">
          {totalDeps} dependenc{totalDeps !== 1 ? "ies" : "y"}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          style={{ borderRadius: 6 }}
          onClick={() => setModalOpen(true)}
        >
          <i className="ti ti-plus me-1" />
          Add Predecessor
        </button>
      </div>

      {loading && totalDeps === 0 ? (
        <div className="text-center py-2">
          <Spin size="small" />
        </div>
      ) : totalDeps === 0 ? (
        <p className="text-muted small mb-0">No dependencies.</p>
      ) : (
        <div className="d-flex flex-column gap-3">
          {predecessors.length > 0 && (
            <div>
              <div className="small fw-semibold text-muted mb-1">
                Predecessors (Must finish before)
              </div>
              <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
                {predecessors.map((dep) => (
                  <li
                    key={dep.id}
                    className="d-flex align-items-center gap-2 p-2"
                    style={{
                      borderRadius: 8,
                      border: "1px solid #e2e8f0",
                      fontSize: 13,
                    }}
                  >
                    {renderIssueRow(Number(dep.predecessor_id))}
                    <Tag color="blue" style={{ margin: 0 }}>
                      {dep.link_type} {dep.lag_days ? `+${dep.lag_days}d` : ""}
                    </Tag>
                    <Tooltip title="Remove dependency">
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        style={{ borderRadius: 6 }}
                        onClick={() => handleDeleteDependency(dep.id)}
                      >
                        <i className="ti ti-x" style={{ color: "#ef4444" }} />
                      </button>
                    </Tooltip>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {successors.length > 0 && (
            <div>
              <div className="small fw-semibold text-muted mb-1">
                Successors (Blocked by this)
              </div>
              <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
                {successors.map((dep) => (
                  <li
                    key={dep.id}
                    className="d-flex align-items-center gap-2 p-2"
                    style={{
                      borderRadius: 8,
                      border: "1px solid #e2e8f0",
                      fontSize: 13,
                    }}
                  >
                    {renderIssueRow(Number(dep.successor_id))}
                    <Tag color="purple" style={{ margin: 0 }}>
                      {dep.link_type} {dep.lag_days ? `+${dep.lag_days}d` : ""}
                    </Tag>
                    <Tooltip title="Remove dependency">
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        style={{ borderRadius: 6 }}
                        onClick={() => handleDeleteDependency(dep.id)}
                      >
                        <i className="ti ti-x" style={{ color: "#ef4444" }} />
                      </button>
                    </Tooltip>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <Modal
        {...SETTINGS_MODAL_PROPS}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setSelectedIssueId(null);
          setSearchResults([]);
        }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: "#3b82f618",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#3b82f6",
                fontSize: 15,
              }}
            >
              <PlusOutlined />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                Add Predecessor
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: "#94a3b8",
                  fontWeight: 400,
                }}
              >
                Mark another issue as a predecessor for this one
              </div>
            </div>
          </div>
        }
        footer={
          <div
            style={{
              padding: "14px 28px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <button
              className="btn btn-light border px-4"
              style={{ borderRadius: 8 }}
              onClick={() => {
                setModalOpen(false);
                setSelectedIssueId(null);
                setSearchResults([]);
              }}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              className="btn px-4"
              style={{
                borderRadius: 8,
                background: "#10b981",
                color: "#fff",
                fontWeight: 600,
              }}
              onClick={handleCreateDependency}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Adding...
                </>
              ) : (
                <>
                  <PlusOutlined className="me-2" />
                  Add Dependency
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="mb-3">
          <label className="form-label small fw-semibold">
            Search Predecessor Issue
          </label>
          <Select
            showSearch
            className="w-100"
            placeholder="Type issue key or title..."
            filterOption={false}
            onSearch={runSearch}
            onChange={(v) => setSelectedIssueId(v)}
            value={selectedIssueId}
            loading={searching}
            notFoundContent={
              searching ? <Spin size="small" /> : "No results"
            }
            options={searchResults.map((i) => ({
              value: i.id,
              label: `${i.issue_key} — ${i.title}`,
            }))}
          />
        </div>
        <div className="row g-2 mb-3">
          <div className="col-8">
            <label className="form-label small fw-semibold">Link Type</label>
            <Select
              className="w-100"
              value={linkType}
              onChange={setLinkType}
              options={LINK_TYPES}
            />
          </div>
          <div className="col-4">
            <label className="form-label small fw-semibold">Lag (Days)</label>
            <InputNumber
              className="w-100"
              value={lagDays}
              onChange={(val) => setLagDays(val || 0)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DependencyPanel;
